import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  PencilIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import BulkInvoiceModal from '../components/BulkInvoiceModal';
import StripePaymentForm from '../components/StripePaymentForm';
import PaymentBreakdown from '../components/PaymentBreakdown';
import ErrorBoundary from '../components/ErrorBoundary';
import Budget from './Budget';
import Subscriptions from './Subscriptions';

const Payments = () => {
  const { user, isCoach } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check for tab parameter in URL on mount
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    tabFromUrl === 'subscription' ? 'subscription' : tabFromUrl === 'budget' ? 'budget' : 'invoices'
  );
  
  // Update URL when tab changes (but not on initial mount)
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (activeTab === 'budget' && currentTab !== 'budget') {
      setSearchParams({ tab: 'budget' });
    } else if (activeTab === 'subscription' && currentTab !== 'subscription') {
      setSearchParams({ tab: 'subscription' });
    } else if (activeTab === 'invoices' && (currentTab === 'budget' || currentTab === 'subscription')) {
      setSearchParams({});
    }
  }, [activeTab, searchParams, setSearchParams]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentPlan, setShowPaymentPlan] = useState(null);
  const [selectedPlanMonths, setSelectedPlanMonths] = useState(null);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(null);
  const [stripePaymentConfig, setStripePaymentConfig] = useState(null);
  const [pendingStripePayment, setPendingStripePayment] = useState(null);
  const [isStripeInitializing, setIsStripeInitializing] = useState(false);
  const [paymentPlanAutoCharge, setPaymentPlanAutoCharge] = useState(false);
  const [autoChargeSetupConfig, setAutoChargeSetupConfig] = useState(null);
  const [autoChargeSetupData, setAutoChargeSetupData] = useState(null);
  const [isPreparingAutoChargeSetup, setIsPreparingAutoChargeSetup] = useState(false);

  // Check for Stripe redirect return (3D Secure, etc.) - cleanup URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntent = urlParams.get('payment_intent');
    const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');
    
    if (paymentIntent || paymentIntentClientSecret) {
      // Clean up URL parameters - StripePaymentForm will handle payment verification
      const tab = searchParams.get('tab');
      const newUrl = window.location.pathname + (tab ? `?tab=${tab}` : '');
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [searchParams]);
  const [showBulkInvoice, setShowBulkInvoice] = useState(false);

  // Fetch invoices data
  useEffect(() => {
    const fetchInvoicesData = async () => {
      try {
        setLoading(true);
        
        // Fetch invoices (for parents/players, this will show invoices sent TO them or their players)
        const invoicesResponse = await fetch('/api/invoices/my-invoices', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (invoicesResponse.ok) {
          const invoicesData = await invoicesResponse.json();
          setInvoices(invoicesData);
        }
      } catch (error) {
        console.error('Error fetching invoices data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchInvoicesData();
    }
  }, [user]);


  const totalOutstanding = invoices.reduce((sum, invoice) => {
    if (invoice.status !== 'paid' && invoice.status !== 'cancelled') {
      const amt = parseFloat(invoice.total_amount);
      return sum + (isNaN(amt) ? 0 : amt);
    }
    return sum;
  }, 0);

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount === null || numAmount === undefined) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'overdue':
        return 'text-red-600 bg-red-100';
      case 'sent':
        return 'text-blue-600 bg-blue-100';
      case 'draft':
        return 'text-gray-600 bg-gray-100';
      case 'cancelled':
        return 'text-gray-500 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const handlePaymentSuccess = (message = 'Payment successful! Thank you for your payment.') => {
    toast.success(message);
    setStripePaymentConfig(null);
    setPendingStripePayment(null);
    setIsStripeInitializing(false);
    
    // Refresh invoices to show updated status
    const fetchInvoicesData = async () => {
      try {
        const response = await fetch('/api/invoices/my-invoices', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const invoicesData = await response.json();
          setInvoices(invoicesData);
        }
      } catch (error) {
        console.error('Error refreshing invoices:', error);
      }
    };
    
    fetchInvoicesData();
  };

  const startStripePayment = async (invoice) => {
    if (isStripeInitializing) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('You must be logged in to pay this invoice.');
      return;
    }

    setIsStripeInitializing(true);
    try {
      const response = await fetch('/api/payments/stripe/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invoice_id: invoice.id
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to start card payment.');
      }

      setStripePaymentConfig({
        clientSecret: data.clientSecret,
        publishableKey: data.publishableKey,
        amount: data.amount,
        currency: data.currency || 'usd',
        breakdown: data.breakdown || null
      });
      setPendingStripePayment({
        invoice,
        paymentIntentId: data.paymentIntentId,
        environment: data.environment || 'test'
      });
    } catch (error) {
      console.error('Error starting Stripe payment:', error);
      toast.error(error.message || 'Unable to start card payment. Please try another method.');
      setStripePaymentConfig(null);
      setPendingStripePayment(null);
    } finally {
      setIsStripeInitializing(false);
    }
  };

  const cancelStripePayment = () => {
    setStripePaymentConfig(null);
    setPendingStripePayment(null);
    setIsStripeInitializing(false);
  };

  const handleStripePaymentComplete = async (result) => {
    if (!pendingStripePayment) {
      return;
    }

    if (!result?.success || !result.paymentIntent) {
      if (result?.error) {
        toast.error(result.error || 'Card payment was not completed.');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/payments/stripe/confirm-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invoice_id: pendingStripePayment.invoice.id,
          payment_intent_id: result.paymentIntent.id || pendingStripePayment.paymentIntentId
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to confirm card payment.');
      }

      handlePaymentSuccess();
    } catch (error) {
      console.error('Error confirming Stripe payment:', error);
      toast.error(error.message || 'Failed to confirm card payment. Please contact support.');
    } finally {
      setStripePaymentConfig(null);
      setPendingStripePayment(null);
    }
  };

  const handleBulkInvoiceSuccess = () => {
    // Refresh invoices after bulk invoice creation
    const fetchInvoicesData = async () => {
      try {
        const response = await fetch('/api/invoices/my-invoices', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const invoicesData = await response.json();
          setInvoices(invoicesData);
        }
      } catch (error) {
        console.error('Error refreshing invoices:', error);
      }
    };
    
    fetchInvoicesData();
  };

  const resetPaymentPlanModalState = () => {
    setShowPaymentPlan(null);
    setSelectedPlanMonths(null);
    setPaymentPlanAutoCharge(false);
    setAutoChargeSetupConfig(null);
    setAutoChargeSetupData(null);
    setIsPreparingAutoChargeSetup(false);
  };

  const startAutoChargeSetup = async () => {
    if (!showPaymentPlan || !selectedPlanMonths || selectedPlanMonths === 'full') return;
    if (!paymentPlanAutoCharge) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('You must be logged in to save a payment method.');
      return;
    }

    setIsPreparingAutoChargeSetup(true);
    try {
      const response = await fetch(`/api/invoices/${showPaymentPlan.id}/payment-plan/setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          installments: parseInt(selectedPlanMonths, 10)
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to initialize card setup.');
      }

      setAutoChargeSetupConfig({
        clientSecret: data.clientSecret,
        publishableKey: data.publishableKey,
        amount: Math.round(Number(showPaymentPlan.total_amount || 0) * 100),
        currency: 'usd',
        breakdown: data.breakdown || null
      });
      setAutoChargeSetupData({
        customerId: data.customerId,
        setupIntentId: data.setupIntentId,
        paymentMethodId: null
      });
    } catch (error) {
      console.error('Error starting auto-charge setup:', error);
      toast.error(error.message || 'Failed to initialize auto-charge setup.');
    } finally {
      setIsPreparingAutoChargeSetup(false);
    }
  };

  const handleAutoChargeSetupComplete = (result) => {
    if (!result?.success || !result.setupIntent) {
      if (result?.error) {
        toast.error(result.error);
      }
      return;
    }

    const paymentMethodId = result.setupIntent.payment_method;
    if (!paymentMethodId) {
      toast.error('Saved card was missing a payment method identifier. Please try again.');
      return;
    }

    setAutoChargeSetupData((prev) => ({
      ...(prev || {}),
      paymentMethodId
    }));
    setAutoChargeSetupConfig(null);
    toast.success('Card saved. Future installments will auto-charge when due.');
  };

  const handlePaymentPlanSelection = async () => {
    if (!showPaymentPlan || !selectedPlanMonths) return;

    try {
      if (selectedPlanMonths === 'full') {
        startStripePayment(showPaymentPlan);
      } else {
        if (paymentPlanAutoCharge && !autoChargeSetupData?.paymentMethodId) {
          toast.error('Save a card first to enable auto-charge.');
          return;
        }

        // Create payment plan via API
        const response = await fetch(`/api/invoices/${showPaymentPlan.id}/select-payment-plan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            installments: parseInt(selectedPlanMonths, 10),
            auto_charge: paymentPlanAutoCharge,
            stripe_customer_id: paymentPlanAutoCharge ? autoChargeSetupData?.customerId : undefined,
            payment_method_id: paymentPlanAutoCharge ? autoChargeSetupData?.paymentMethodId : undefined
          })
        });

        if (response.ok) {
          const result = await response.json();
          const installmentCount = Array.isArray(result.installments)
            ? result.installments.length
            : 0;
          const autopayEnabled = result?.autopay?.enabled;
          toast.success(`Payment plan created! ${installmentCount} installment invoices have been generated.${autopayEnabled ? ' Auto-charge has been enabled.' : ''}`);
          
          // Refresh invoices
          const invoicesResponse = await fetch('/api/invoices/my-invoices', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (invoicesResponse.ok) {
            const invoicesData = await invoicesResponse.json();
            setInvoices(invoicesData);
          }
          
          resetPaymentPlanModalState();
        } else {
          // Try to get error message, but handle cases where response isn't JSON
          let errorMessage = 'Failed to create payment plan';
          try {
            const error = await response.json();
            errorMessage = error.message || errorMessage;
          } catch (e) {
            // If response isn't JSON, show the status text
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
          toast.error(`Error: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error selecting payment plan:', error);
      toast.error('Error creating payment plan. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
              <p className="mt-2 text-gray-600">
                {user?.role === 'org_admin' 
                  ? 'Manage organization-wide invoicing and payments' 
                  : isCoach 
                    ? 'Manage team payments and invoices' 
                    : 'View and pay your invoices'
                }
              </p>
            </div>
            {(isCoach || user?.role === 'org_admin') && activeTab === 'invoices' && (
              <div className="flex space-x-3">
                {user?.role === 'org_admin' && (
                  <button
                    onClick={() => setShowBulkInvoice(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Bulk Invoice Team
                  </button>
                )}
                {isCoach && (
                  <button
                    onClick={() => window.location.href = '/invoicing'}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Invoice
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <CreditCardIcon className="h-5 w-5 mr-2" />
                Invoices
              </div>
            </button>
            <button
              onClick={() => setActiveTab('budget')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'budget'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                Budget
              </div>
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscription'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                Subscription
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'subscription' ? (
          <Subscriptions />
        ) : activeTab === 'budget' ? (
          <Budget />
        ) : (
          <>
            {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CreditCardIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Outstanding
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(totalOutstanding)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Overdue Invoices
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {invoices.filter(inv => inv.status === 'overdue').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Paid Invoices
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {invoices.filter(inv => inv.status === 'paid').length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices Section */}
        {invoices.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Invoices</h2>
              <p className="text-sm text-gray-600 mt-1">
                {user?.role === 'org_admin'
                  ? 'Invoices you have created for organization teams'
                  : isCoach 
                    ? 'Invoices you have sent to team members and any invoices sent to you' 
                    : 'Invoices sent to you or your players'
                }
              </p>
            </div>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-gray-200">
              {invoices.map((invoice) => {
                const isPaid = invoice.status === 'paid';
                const isCancelled = invoice.status === 'cancelled';
                const isOwnInvoice = isCoach && parseInt(invoice.from_user_id) === parseInt(user.id);
                return (
                  <div key={invoice.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{invoice.title}</p>
                        <p className="text-xs text-gray-500">#{invoice.invoice_number} · Due {formatDate(invoice.due_date)}</p>
                        {invoice.ToUser?.id !== user.id && invoice.ToUser?.role === 'player' && (
                          <p className="text-xs text-blue-600 font-medium mt-0.5">👤 {invoice.ToUser.name}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-gray-900">{formatCurrency(invoice.total_amount)}</p>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                        {invoice.is_installment && (
                          <p className="text-xs text-gray-500 mt-0.5">{invoice.installment_number}/{invoice.total_installments}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {isOwnInvoice ? (
                        <>
                          <button onClick={() => setShowInvoiceDetails(invoice)} className="flex items-center text-sm text-blue-600 font-medium">
                            <EyeIcon className="h-4 w-4 mr-1" />View
                          </button>
                          <button onClick={() => window.location.href = `/invoicing?edit=${invoice.id}`} className="flex items-center text-sm text-gray-600 font-medium">
                            <PencilIcon className="h-4 w-4 mr-1" />Edit
                          </button>
                          {isPaid && <span className="flex items-center text-sm text-green-600 font-medium"><CheckCircleIcon className="h-4 w-4 mr-1" />Paid</span>}
                        </>
                      ) : isPaid ? (
                        <span className="text-sm text-green-600 font-medium">✓ Paid</span>
                      ) : !isCancelled && (
                        invoice.payment_plan_enabled && !invoice.is_installment ? (
                          <button
                            onClick={() => { setShowPaymentPlan(invoice); setSelectedPlanMonths(invoice.payment_plan_options?.[0] || 3); setPaymentPlanAutoCharge(false); setAutoChargeSetupConfig(null); setAutoChargeSetupData(null); }}
                            className="w-full py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                          >
                            Choose Payment Plan
                          </button>
                        ) : (
                          <button
                            onClick={() => startStripePayment(invoice)}
                            className="w-full py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                          >
                            Pay Now
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{invoice.title}</div>
                          <div className="text-sm text-gray-500">#{invoice.invoice_number}</div>
                          {invoice.ToUser?.id !== user.id && invoice.ToUser?.role === 'player' && (
                            <div className="text-xs text-blue-600 mt-1 font-medium">👤 For: {invoice.ToUser.name}</div>
                          )}
                          {invoice.description && (
                            <div className="text-sm text-gray-500 mt-1">{invoice.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(invoice.total_amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(invoice.due_date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                        {invoice.is_installment && (
                          <div className="text-xs text-gray-500 mt-1">
                            Installment {invoice.installment_number}/{invoice.total_installments}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {isCoach ? (
                            parseInt(invoice.from_user_id) === parseInt(user.id) ? (
                              <>
                                <button onClick={() => setShowInvoiceDetails(invoice)} className="text-blue-600 hover:text-blue-900 font-medium flex items-center">
                                  <EyeIcon className="h-4 w-4 mr-1" />View
                                </button>
                                <button onClick={() => window.location.href = `/invoicing?edit=${invoice.id}`} className="text-gray-600 hover:text-gray-900 font-medium flex items-center">
                                  <PencilIcon className="h-4 w-4 mr-1" />Edit
                                </button>
                                {invoice.status === 'paid' && (
                                  <span className="text-green-600 font-medium flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" />Paid</span>
                                )}
                              </>
                            ) : (
                              <>
                                {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                  invoice.payment_plan_enabled && !invoice.is_installment ? (
                                    <button onClick={() => { setShowPaymentPlan(invoice); setSelectedPlanMonths(invoice.payment_plan_options?.[0] || 3); setPaymentPlanAutoCharge(false); setAutoChargeSetupConfig(null); setAutoChargeSetupData(null); }} className="text-blue-600 hover:text-blue-900 font-medium">
                                      Choose Payment Plan
                                    </button>
                                  ) : (
                                    <button onClick={() => startStripePayment(invoice)} className="text-primary-600 hover:text-primary-900 font-medium">Pay Now</button>
                                  )
                                )}
                                {invoice.status === 'paid' && <span className="text-green-600 font-medium">✓ Paid</span>}
                              </>
                            )
                          ) : (
                            <>
                              {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                invoice.payment_plan_enabled && !invoice.is_installment ? (
                                  <button onClick={() => { setShowPaymentPlan(invoice); setSelectedPlanMonths(invoice.payment_plan_options?.[0] || 3); setPaymentPlanAutoCharge(false); setAutoChargeSetupConfig(null); setAutoChargeSetupData(null); }} className="text-blue-600 hover:text-blue-900 font-medium">
                                    Choose Payment Plan
                                  </button>
                                ) : (
                                  <button onClick={() => startStripePayment(invoice)} className="text-primary-600 hover:text-primary-900 font-medium">Pay Now</button>
                                )
                              )}
                              {invoice.status === 'paid' && <span className="text-green-600 font-medium">✓ Paid</span>}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Invoices Message */}
        {invoices.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoices</h3>
            <p className="text-gray-600">
              {isCoach 
                ? "You haven't created any invoices yet. Create your first invoice to get started."
                : "You don't have any invoices at this time."
              }
            </p>
            {isCoach && (
              <button
                onClick={() => window.location.href = '/invoicing'}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Invoice
              </button>
            )}
          </div>
        )}

        {/* Invoice Details Modal for Coaches */}
        {showInvoiceDetails && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-end sm:items-start justify-center overflow-y-auto">
            <div className="relative w-full max-w-2xl mx-auto p-5 border shadow-lg rounded-t-2xl sm:rounded-md bg-white mt-auto sm:mt-20 mb-0 sm:mb-8 max-h-[90dvh] overflow-y-auto">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Invoice Details</h3>
                  <button
                    onClick={() => setShowInvoiceDetails(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Invoice Header */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Invoice Number</p>
                        <p className="font-medium text-gray-900">#{showInvoiceDetails.invoice_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(showInvoiceDetails.status)}`}>
                          {showInvoiceDetails.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="font-medium text-gray-900">{formatCurrency(showInvoiceDetails.total_amount)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Due Date</p>
                        <p className="font-medium text-gray-900">{formatDate(showInvoiceDetails.due_date)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{showInvoiceDetails.title}</h4>
                    {showInvoiceDetails.description && (
                      <p className="text-gray-600 mb-4">{showInvoiceDetails.description}</p>
                    )}
                  </div>

                  {/* Recipient Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Recipient</h4>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium text-gray-900">{showInvoiceDetails.ToUser?.name}</p>
                      <p className="text-sm text-gray-600">{showInvoiceDetails.ToUser?.email}</p>
                      <p className="text-sm text-gray-600 capitalize">{showInvoiceDetails.ToUser?.role}</p>
                    </div>
                  </div>

                  {/* Line Items */}
                  {showInvoiceDetails.line_items && showInvoiceDetails.line_items.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Line Items</h4>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        {showInvoiceDetails.line_items.map((item, index) => (
                          <div key={index} className="flex justify-between items-center py-1">
                            <span className="text-gray-900">{item.description}</span>
                            <span className="font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                          </div>
                        ))}
                        {showInvoiceDetails.tax_amount > 0 && (
                          <div className="flex justify-between items-center py-1 border-t border-gray-200 mt-2 pt-2">
                            <span className="text-gray-900">Tax</span>
                            <span className="font-medium text-gray-900">{formatCurrency(showInvoiceDetails.tax_amount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center py-1 border-t border-gray-200 mt-2 pt-2 font-bold">
                          <span className="text-gray-900">Total</span>
                          <span className="text-gray-900">{formatCurrency(showInvoiceDetails.total_amount)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payment Information */}
                  {showInvoiceDetails.status === 'paid' && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Payment Information</h4>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">
                          <strong>Paid on:</strong> {formatDate(showInvoiceDetails.paid_date)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Payment Method:</strong> {showInvoiceDetails.payment_method || 'Unknown'}
                        </p>
                        {showInvoiceDetails.payment_reference && (
                          <p className="text-sm text-gray-600">
                            <strong>Reference:</strong> {showInvoiceDetails.payment_reference}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {showInvoiceDetails.notes && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-600 whitespace-pre-wrap">{showInvoiceDetails.notes}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={() => setShowInvoiceDetails(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => window.location.href = `/invoicing?edit=${showInvoiceDetails.id}`}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Invoice
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Plan Selection Modal */}
        {showPaymentPlan && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-end sm:items-start justify-center overflow-y-auto">
            <div className="relative w-full max-w-md mx-auto p-5 border shadow-lg rounded-t-2xl sm:rounded-md bg-white mt-auto sm:mt-20 mb-0 sm:mb-8 max-h-[90dvh] overflow-y-auto">
              <div className="mt-3 pb-24 sm:pb-0">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Select Payment Plan</h3>
                <div className="space-y-4">
                  {/* Invoice Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900">{showPaymentPlan.title}</h4>
                    <p className="text-sm text-gray-600">#{showPaymentPlan.invoice_number}</p>
                    <p className="text-lg font-bold text-gray-900 mt-2">
                      Total: {formatCurrency(showPaymentPlan.total_amount)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Due: {formatDate(showPaymentPlan.due_date)}
                    </p>
                  </div>

                  {/* Payment Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Choose Payment Option</label>

                    {/* Pay in Full Option */}
                    <div className="border-2 border-gray-200 rounded-lg p-4 mb-3 hover:border-blue-500 cursor-pointer">
                      <div className="flex items-start">
                        <input
                          type="radio"
                          name="paymentOption"
                          value="full"
                          checked={selectedPlanMonths === 'full'}
                          onChange={() => {
                            setSelectedPlanMonths('full');
                            setPaymentPlanAutoCharge(false);
                            setAutoChargeSetupConfig(null);
                            setAutoChargeSetupData(null);
                          }}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <label className="block text-sm font-medium text-gray-900">Pay in Full</label>
                          <p className="text-sm text-gray-600">Pay the entire amount today</p>
                          <p className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(showPaymentPlan.total_amount)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Plan Options */}
                    {showPaymentPlan.payment_plan_options?.map((months) => {
                      const monthlyAmount = parseFloat((showPaymentPlan.total_amount / months).toFixed(2));
                      return (
                        <div key={months} className="border-2 border-gray-200 rounded-lg p-4 mb-3 hover:border-blue-500 cursor-pointer">
                          <div className="flex items-start">
                            <input
                              type="radio"
                              name="paymentOption"
                              value={months}
                              checked={selectedPlanMonths === months}
                              onChange={() => {
                                setSelectedPlanMonths(months);
                                setPaymentPlanAutoCharge(false);
                                setAutoChargeSetupConfig(null);
                                setAutoChargeSetupData(null);
                              }}
                              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <div className="ml-3 flex-1">
                              <label className="block text-sm font-medium text-gray-900">
                                {months} Monthly Payments
                              </label>
                              <p className="text-sm text-gray-600">
                                Spread payments over {months} months
                              </p>
                              <p className="text-lg font-bold text-gray-900 mt-1">
                                {formatCurrency(monthlyAmount)}/month
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Total: {formatCurrency(showPaymentPlan.total_amount)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedPlanMonths !== 'full' && selectedPlanMonths && (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="autoChargePlan"
                          checked={paymentPlanAutoCharge}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPaymentPlanAutoCharge(checked);
                            if (!checked) {
                              setAutoChargeSetupConfig(null);
                              setAutoChargeSetupData(null);
                            }
                          }}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label htmlFor="autoChargePlan" className="ml-3">
                          <span className="block text-sm font-medium text-gray-900">Automatically charge saved card for each installment</span>
                          <span className="block text-xs text-gray-600 mt-1">You can turn this off later by contacting your coach/admin.</span>
                        </label>
                      </div>

                      {paymentPlanAutoCharge && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={startAutoChargeSetup}
                            disabled={isPreparingAutoChargeSetup}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isPreparingAutoChargeSetup
                              ? 'Preparing secure form...'
                              : autoChargeSetupData?.paymentMethodId
                                ? 'Replace Saved Card'
                                : 'Save Card for Auto-Charge'}
                          </button>
                          {autoChargeSetupData?.paymentMethodId && (
                            <p className="text-xs text-green-700 mt-2">Card saved and ready for auto-charge.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> If you choose a payment plan, multiple installment invoices will be created with monthly due dates.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetPaymentPlanModalState}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePaymentPlanSelection}
                      disabled={
                        !selectedPlanMonths ||
                        (selectedPlanMonths !== 'full' && paymentPlanAutoCharge && !autoChargeSetupData?.paymentMethodId)
                      }
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {selectedPlanMonths === 'full' ? 'Continue to Payment' : 'Confirm Payment Plan'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {autoChargeSetupConfig && (
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-end sm:items-start justify-center overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setAutoChargeSetupConfig(null);
              }
            }}
          >
            <div
              className="relative w-full max-w-md mx-auto p-4 sm:p-5 border shadow-lg rounded-t-2xl sm:rounded-md bg-white mt-auto sm:mt-20 mb-0 sm:mb-8 max-h-[90dvh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mt-3 pb-24 sm:pb-0">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Save Card for Auto-Charge</h3>
                    <p className="text-sm text-gray-600">Your card will be charged automatically for plan installments when due.</p>
                  </div>
                  <button
                    onClick={() => setAutoChargeSetupConfig(null)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {autoChargeSetupConfig.breakdown?.processingFee > 0 && (
                  <p className="mb-3 text-sm text-gray-600">
                    Each automatic charge includes a card processing fee of about 2.9% + 30¢
                    (${Number(autoChargeSetupConfig.breakdown.processingFee).toFixed(2)} on the full ${Number(autoChargeSetupConfig.breakdown.baseAmount).toFixed(2)} balance).
                  </p>
                )}
                <div className="min-h-[300px]">
                  <ErrorBoundary>
                    <StripePaymentForm
                      config={autoChargeSetupConfig}
                      onComplete={handleAutoChargeSetupComplete}
                      mode="setup"
                      buttonLabel="Save Card"
                    />
                  </ErrorBoundary>
                </div>
                <div className="mt-4 text-xs text-gray-500">
                  Stored securely by Stripe. FieldHouse does not store full card numbers.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stripe Payment Modal */}
        {pendingStripePayment && stripePaymentConfig && (
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-end sm:items-start justify-center overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                cancelStripePayment();
              }
            }}
          >
            <div
              className="relative w-full max-w-md mx-auto p-4 sm:p-5 border shadow-lg rounded-t-2xl sm:rounded-md bg-white mt-auto sm:mt-20 mb-0 sm:mb-8 max-h-[90dvh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mt-3 pb-24 sm:pb-0">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Pay Invoice #{pendingStripePayment.invoice.invoice_number}</h3>
                    <p className="text-sm text-gray-600">
                      {pendingStripePayment.invoice.title} • {formatCurrency(pendingStripePayment.invoice.total_amount)}
                    </p>
                  </div>
                  <button
                    onClick={cancelStripePayment}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <PaymentBreakdown breakdown={stripePaymentConfig.breakdown} baseLabel="Invoice amount" />
                <div className="min-h-[300px]">
                  <ErrorBoundary>
                    <StripePaymentForm
                      config={stripePaymentConfig}
                      onComplete={handleStripePaymentComplete}
                      buttonLabel={`Pay ${(stripePaymentConfig.amount / 100).toFixed(2)} ${stripePaymentConfig.currency.toUpperCase()}`}
                    />
                  </ErrorBoundary>
                </div>
                <div className="mt-4 text-xs text-gray-500">
                  Secure payment powered by Stripe.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Invoice Modal */}
        <BulkInvoiceModal
          isOpen={showBulkInvoice}
          onClose={() => setShowBulkInvoice(false)}
          onSuccess={handleBulkInvoiceSuccess}
        />
          </>
        )}
      </div>
    </div>
  );
};

export default Payments;
